import { Col, DatePicker, Form, Input, InputNumber, message, Modal, Row, Select, Switch, Upload } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getallProject, getallProjectForReceipts, getAllReceipts, getCustomers, getReceiptsByID, updateReceipts } from "../../utils/API";
import { ACCOUNTS } from "../../utils/accounts";

interface EditReceiptsProps {
    visible: boolean;
    id: string;
    onClose?: () => void;
    onCancel?: () => void;
    onTaskCreated?: () => void;
    onSuccess?: () => void;
    // record?: ReceiptsFormValues | null;
}

interface ReceiptsFormValues {
    _id: string;
    [key: string]: any;
    receipts_number: string;
    project: string;
    customer_name: string;
    amount: number;
    method: string;
    payment_date: string;
    collected_by: string;
    authorize: boolean;
    description: string;
}

const EditReceiptsModel: React.FC<EditReceiptsProps> = (props) => {
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const [project, setProject] = useState<any>(null);
    const [customers, setCustomers] = useState<any>([]);
    const [receiptParts, setReceiptParts] = useState({
        prefix: "",
        year: "",
        seq: "",
    });
    const [projects, setProjects] = useState<any[]>([]);
    // ponytail: hardcoded account list, no backend fetch.
    const accounts = ACCOUNTS;

    const onFinish = async (values: ReceiptsFormValues) => {
        setIsLoading(true);
        try {
            const formData = new FormData();

            const relatedProject = projects.find(
                (p: any) => p._id === values.project
            );

            if (values.payment_date) {
                values.payment_date = dayjs(values.payment_date).format('DD-MM-YYYY'); // Change format as needed
            }

            if (values.authorize === undefined) {
                values.authorize = false;  // Default value in case it's undefined
            }

            const finalValues = {
                ...values,
                project: relatedProject ? values.project : null,
            };

            if (props.id) {
                await updateReceipts(props.id, finalValues);
                message.success("Receipts updated successfully!");
            }
            getAllReceipts("");
            props.onSuccess?.();
        } catch (error: any) {
            message.error(error.response?.data?.message || "Failed to save receipts");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (project) {
            const parts = parseReceiptNumber(project.receipts_number);
            setReceiptParts(parts);

            form.setFieldsValue({
                customer_name: project.customer_name,
                project: project.project || undefined,
                receipts_number: project.receipts_number,
                amount: project.amount,
                method: project.method,
                payment_date: project.payment_date ? dayjs(project.payment_date, "DD-MM-YYYY") : null,
                collected_by: project.collected_by,
                authorize: project.authorize,
                description: project.description,
                account: project.account ?? 1,
            });
        }
    }, [project, form]);


    const fetchReceipts = async () => {
        setIsLoading(true);
        try {
            if (props.id) {
                const data = await getReceiptsByID(props.id);
                setProject(data);
            }
        } catch (error) {
            message.error("Failed to fetch receipts data.");
        } finally {
            setIsLoading(false);
        }
    }

    const fetchProjects = async () => {
        setIsLoading(true);
        try {
            const response: any = await getallProjectForReceipts("", "", true);

            if (response?.data) {
                const activeProjects = response.data.filter(
                    (client: any) => client.isActive === true
                );
                setProjects(activeProjects);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setIsLoading(false);
        }
    }

    const parseReceiptNumber: any = (receipt: string) => {
        if (!receipt) return { prefix: "", year: "", seq: "" };

        const parts = receipt.split("-");
        return {
            prefix: parts[0] || "",
            year: parts[1] || "",
            seq: parts[2] || "",
        };
    };

    useEffect(() => {
        fetchReceipts();
        fetchProjects();
    }, [props.id]);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response: any = await getCustomers("", "");

                if (response?.data) {
                    // Filter only active clients
                    const activeCustomers = response.data.filter(
                        (client: any) => client.isActive === true
                    );
                    setCustomers(activeCustomers);
                }
            } catch (error) {
                console.error("Error fetching customers:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCustomers();
    }, []);

    // Watch the currently selected project
    const selectedProjectId = Form.useWatch("project", form);

    // Find the project object
    const selectedProject = projects.find((p: any) => p._id === selectedProjectId);

    // Filter customers: only show the customer linked to selected project
    const filteredCustomers = selectedProject
        ? customers.filter((c: any) => c._id === selectedProject.clientId?._id)
        : [];

    return (
        <Modal
            visible={props.visible}
            onCancel={props.onClose}
            footer={null}
            width={800}
            centered={true}
            destroyOnClose
            className="custom-modal"
            bodyStyle={{
                overflowY: "auto",
                fontFamily: "inter, sans-serif",
            }}
            style={{
                bottom: 20,
                top: 20,
                // maxHeight: 'calc(100vh - 30px)',
                margin: '0 auto',
                position: 'relative',
            }}
            styles={{
                header: {
                    padding: "12px",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#f1f5ffff",
                },
                content: {
                    padding: 0,
                    overflow: "hidden",
                },
            }}
            title={
                <div
                    style={{
                        color: "#4b5563",
                        fontFamily: "inter, sans-serif",
                        fontSize: "20px",
                        fontWeight: 500,
                    }}
                >
                    Edit Receipts
                </div>
            }
        >
            <Form onFinish={onFinish} form={form} layout="horizontal"
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
                colon={false} style={{ margin: 0 }}>
                <div className="elements">
                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Project
                            </span>
                        }
                        name="project"
                        required={false}
                    >
                        <Select
                            size="large"
                            allowClear
                            // disabled={roleId === 2}
                            showSearch
                            optionFilterProp="children"
                            loading={isLoading}
                            placeholder="Select Project"
                            className="CustomSelect"
                            onChange={(projectId) => {
                                const selectedProject = projects.find(
                                    (p: any) => p._id === projectId
                                );

                                if (selectedProject?.clientId?._id) {
                                    form.setFieldsValue({
                                        customer_name: selectedProject.clientId?._id
                                    });
                                }
                            }}
                        >
                            {projects.map((project: any) => (
                                <Select.Option key={project._id} value={project._id}>
                                    {project.projectName}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Customer Name
                            </span>
                        }
                        name="customer_name"
                        required={false}
                    >
                        <Select
                            size="large"
                            // disabled={roleId === 2}
                            showSearch
                            optionFilterProp="children"
                            loading={isLoading}
                            placeholder="Select Customer Name"
                            className="CustomSelect"
                            onChange={(customerId) => {
                                const relatedProject = projects.find(
                                    (p: any) => p.clientId?._id === customerId
                                );

                                if (relatedProject) {
                                    form.setFieldsValue({
                                        project: relatedProject._id,
                                    });
                                } else {
                                    form.resetFields(["project"]);
                                }
                            }}
                        >
                            {customers.map((customer: any) => (
                                <Select.Option key={customer._id} value={customer._id}>
                                    {customer.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Receipts Number
                            </span>
                        }
                    >
                        <Row gutter={8}>
                            <Col span={4}>
                                <Input
                                    value={receiptParts.prefix}
                                    disabled
                                    className="Allinput text-[14px]"
                                />
                            </Col>

                            <Col span={4}>
                                <Input
                                    value={receiptParts.year}
                                    disabled
                                    className="Allinput text-[14px]"
                                />
                            </Col>

                            <Col span={12}>
                                <Input
                                    value={receiptParts.seq}
                                    disabled
                                    className="Allinput"
                                />
                            </Col>
                        </Row>

                        {/* ðŸ”¥ Hidden field that is actually submitted */}
                        <Form.Item name="receipts_number" hidden>
                            <Input />
                        </Form.Item>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Payment Date
                            </span>
                        }
                        name="payment_date"
                        required={false}
                    >
                        <DatePicker className="Allinput" format="DD-MM-YYYY" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Account Name
                            </span>
                        }
                        name="account"
                        required={false}
                        rules={[{ required: true, message: "Please select an account" }]}
                    >
                        <Select
                            placeholder="Select Account"
                            className="CustomSelect"
                            optionLabelProp="label"
                        >
                            {accounts.map((acc: any) => (
                                <Select.Option key={acc.value} value={acc.value} label={`${acc.name}`}>
                                    {acc.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Amount
                            </span>
                        }
                        name="amount"
                        required={false}
                    >
                        <Input type="Number" placeholder="Amount" className="Allinput" />
                    </Form.Item>

                    <Form.Item
                        name="method"
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Method
                            </span>
                        }
                        required={false}
                    >
                        <Select
                            placeholder="Select Method"
                            className="CustomSelect"
                            showSearch
                            optionFilterProp="children"
                            // value={position}
                            onChange={(value) => {
                                // setPosition(value);
                                form.setFieldsValue({ technology: undefined });
                            }}
                        >
                            <Select.Option value="Bank">Bank</Select.Option>
                            <Select.Option value="Cash">Cash</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="collected_by"
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Collected By
                            </span>
                        }
                        required={false}
                    >
                        <Select
                            placeholder="Select Collected By"
                            className="CustomSelect"
                            // value={position}
                            onChange={(value) => {
                                // setPosition(value);
                                form.setFieldsValue({ technology: undefined });
                            }}
                        >
                            <Select.Option value="System">System</Select.Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="authorize"
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Authorized?
                            </span>
                        }
                        valuePropName="checked"     // ðŸ‘ˆ important for Switch
                    >
                        <Switch
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                        />
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Description <span className="text-red-500">*</span>
                            </span>
                        }
                        name="description"
                        required={false}
                        rules={[{ required: true, message: "Please select the project" }]}
                    >
                        <Input.TextArea
                            placeholder="Enter description"
                            className="Allinputtextarea"
                        />
                    </Form.Item>
                </div>
                <Row justify="end">
                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        xl={24}
                        xxl={20}
                        className="d-flex justify-content-end me-4"
                    >
                        <div className="button-wrapper">
                            <button
                                className="allcanclebutton"
                                onClick={props.onClose}
                                type="button"
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className="allsubmitebutton w-20"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Updating...' : 'Update'}
                            </button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default EditReceiptsModel;


